import { SoftwallRecoveredType } from "../types";
import {
  getLastRenderDate,
  restoreRenderDate,
  saveRecovery,
  setAttributionExpiry,
} from "../data";
import { apiDomain, bt_env } from "../../settings";
import { RL_PREVIEW } from "./queryParams";
import { log, shouldLogByProbability } from "./logs";

type UserAttrReq = {
  renderDate: Date;
  recoveryType?: SoftwallRecoveredType;
  attributionExpiry?: number; // expiration time in milliseconds
};

export async function setServerSideUserState(
  recoveryType?: SoftwallRecoveredType,
  attributionExpiry?: number,
): Promise<void> {
  if (RL_PREVIEW) {
    // prevent call state API on preview
    return Promise.resolve();
  }

  return getLastRenderDate().then((lastRenderDate) => {
    const renderDate = lastRenderDate || new Date();
    callStateAPI({
      recoveryType,
      renderDate,
      attributionExpiry,
    }).catch((error) => {
      if (shouldLogByProbability()) {
        log(
          "rlink.setServerSideUserStateError",
          {
            aaResults: window.__bt_intrnl.aaDetectionResults,
            error: error instanceof Error ? error.message : error,
          },
          { severity: 3 },
        );
      }
    });
  });
}

export async function restoreServerSideUserAttr() {
  if (RL_PREVIEW) {
    // prevent call state API on preview
    return Promise.resolve();
  }

  return fetch(`https://${apiDomain}/mw/state?bt_env=${bt_env}`)
    .then((r) => {
      if (!r.ok) {
        throw new Error(
          `Server side attribution request failed. StatusCode: ${r.status}`,
        );
      }
      return r.json();
    })
    .then((userAttr) => {
      const { renderDate, recoveryType, attributionExpiry } = userAttr || {};
      window.__bt_intrnl.recoveryUserAttr = window.__bt_intrnl
        .recoveryUserAttr || {
        mwRendered: false,
        recoveryType: undefined,
        attributionExpiry: undefined,
      };
      if (renderDate || recoveryType || attributionExpiry) {
        // log if back-end respond with any data to restore to local storage
        if (shouldLogByProbability()) {
          log(
            "rlink.restoreServerSideAttribution",
            {
              aaResults: window.__bt_intrnl.aaDetectionResults,
              serverSideAttribution: userAttr,
            },
            { severity: 1 },
          );
        }
      }
      if (renderDate) {
        window.__bt_intrnl.recoveryUserAttr.mwRendered = true;
        restoreRenderDate(renderDate);
      }
      if (recoveryType) {
        window.__bt_intrnl.recoveryUserAttr.recoveryType = recoveryType;
        window.__bt_intrnl.recoveryUserAttr.attributionExpiry =
          attributionExpiry;
        saveRecovery(recoveryType);
        setAttributionExpiry(attributionExpiry);
      }
    })
    .catch(() => null); // to avoid fail on Promise.all
}

function callStateAPI(request: UserAttrReq) {
  return fetch(`https://${apiDomain}/mw/state/recovered?bt_env=${bt_env}`, {
    method: "POST",
    body: JSON.stringify(request),
    headers: {
      "Content-Type": "application/json",
    },
    mode: "no-cors",
  });
}
